import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  Box,
  Typography,
  MenuItem,
  Select,
  ListItemText,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import {
  ControlToggleGroup,
  buildControlMenuProps,
  getControlTokens,
} from '../common/controls';
import { MARKET_ICON_COMPONENTS } from '../../constants/marketIcons';
import { CATEGORY_GROUP_ICONS, DEFAULT_CATEGORY_ICON } from '../../constants/categoryIcons';
import {
  INDUSTRY_ORDER,
  SUB_INDUSTRY_ORDER,
  CATEGORY_ORDER,
  CATEGORY_CHANGE_SOURCES,
  CATEGORY_GRAINS,
  getSubIndustryCategories,
  getCategoryMarkets,
  getCategoryLabelType,
  getBulkTargetForNode,
  getSelectableLabelsForGrain,
  inferCategorySelectionGrain,
  normalizeCategorySelection,
  sanitizeSelectionForGrain,
  toggleCategorySelection,
  toMarketSet,
} from '../../constants/filterCategories';

const SECTOR_INDUSTRIES = {
  software: ['Software'],
  payments: ['Payments & FinTech'],
  infoServices: ['Information Services'],
};

const getInitialSelectionGrain = (selectionDepth) => {
  if (selectionDepth === 'leafOnly' || selectionDepth === CATEGORY_GRAINS.MARKET) {
    return CATEGORY_GRAINS.MARKET;
  }
  if (selectionDepth === CATEGORY_GRAINS.SUB_INDUSTRY) {
    return CATEGORY_GRAINS.SUB_INDUSTRY;
  }
  if (selectionDepth === CATEGORY_GRAINS.INDUSTRY) {
    return CATEGORY_GRAINS.INDUSTRY;
  }
  return CATEGORY_GRAINS.SUB_INDUSTRY;
};

const resolveSelectionGrain = ({ selectedCategory, selectionDepth, includeIndustryGrain }) => {
  const fallbackGrain = getInitialSelectionGrain(selectionDepth);
  const inferredGrain = inferCategorySelectionGrain(selectedCategory, fallbackGrain);
  if (inferredGrain === CATEGORY_GRAINS.INDUSTRY && !includeIndustryGrain) return fallbackGrain;
  return inferredGrain;
};

const buildExpandedState = (grain) => {
  const next = Object.fromEntries(INDUSTRY_ORDER.map(ind => [ind, true]));
  INDUSTRY_ORDER.forEach((industry) => {
    SUB_INDUSTRY_ORDER[industry].forEach((subIndustry) => {
      next[subIndustry] = grain === CATEGORY_GRAINS.CATEGORY || grain === CATEGORY_GRAINS.MARKET;
      CATEGORY_ORDER[industry][subIndustry].forEach((category) => {
        next[category] = next[category] || grain === CATEGORY_GRAINS.MARKET;
      });
    });
  });
  return next;
};

const FilterCategory = ({
  selectedCategory,
  handleCategoryChange,
  activeSector,
  availableMarkets,
  selectionDepth = CATEGORY_GRAINS.SUB_INDUSTRY,
  selectionGrain: controlledSelectionGrain,
  onSelectionGrainChange,
  autoNormalizeSelection = true,
  restrictOptionsToAvailableMarkets = true,
  includeIndustryGrain = false,
  controlContext = 'filterBar',
  menuContext = controlContext,
  width = 170,
}) => {
  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  const controlWidth = typeof width === 'number' ? `${width}px` : width;
  const menuPaperRef = useRef(null);
  const scrollResetFrameRef = useRef(null);
  const scrollResetTimeoutRef = useRef(null);
  const optionAvailableMarkets = restrictOptionsToAvailableMarkets ? availableMarkets : null;
  const optionAvailableMarketSet = useMemo(() => toMarketSet(optionAvailableMarkets), [optionAvailableMarkets]);
  const targetSelectionGrain = useMemo(() => (
    resolveSelectionGrain({ selectedCategory, selectionDepth, includeIndustryGrain })
  ), [includeIndustryGrain, selectedCategory, selectionDepth]);
  const [internalSelectionGrain, setInternalSelectionGrain] = useState(targetSelectionGrain);
  const selectionGrain = controlledSelectionGrain || internalSelectionGrain;
  const setSelectionGrain = useCallback((nextGrain) => {
    setInternalSelectionGrain(nextGrain);
    onSelectionGrainChange?.(nextGrain);
  }, [onSelectionGrainChange]);
  const isMarketGrain = selectionGrain === CATEGORY_GRAINS.MARKET;
  const selectionGrainOptions = useMemo(() => [
    ...(includeIndustryGrain
      ? [{
          value: CATEGORY_GRAINS.INDUSTRY,
          tooltip: 'Select by industry',
          sublabel: 'Industry',
          buttonWidth: 72,
        }]
      : []),
    {
      value: CATEGORY_GRAINS.SUB_INDUSTRY,
      tooltip: 'Select by sub-industry',
      sublabel: 'Sub-industry',
      buttonWidth: 88,
    },
    {
      value: CATEGORY_GRAINS.CATEGORY,
      tooltip: 'Select by category',
      sublabel: 'Category',
      buttonWidth: 72,
    },
    {
      value: CATEGORY_GRAINS.MARKET,
      tooltip: 'Select by market',
      sublabel: 'Market',
      buttonWidth: 60,
    },
  ], [includeIndustryGrain]);

  const [expanded, setExpanded] = useState(() => buildExpandedState(selectionGrain));
  const previousSelectionGrainRef = useRef(selectionGrain);
  const activeIndustries = useMemo(
    () => activeSector && SECTOR_INDUSTRIES[activeSector]
      ? INDUSTRY_ORDER.filter(ind => SECTOR_INDUSTRIES[activeSector].includes(ind))
      : INDUSTRY_ORDER,
    [activeSector]
  );
  const visibleIndustries = useMemo(
    () => activeIndustries.filter(industry =>
      getBulkTargetForNode({ grain: CATEGORY_GRAINS.MARKET, industry, availableMarkets: optionAvailableMarkets }).length > 0
    ),
    [activeIndustries, optionAvailableMarkets]
  );
  const selectableLabels = useMemo(
    () => getSelectableLabelsForGrain(selectionGrain, { availableMarkets: optionAvailableMarkets, industries: activeIndustries }),
    [activeIndustries, optionAvailableMarkets, selectionGrain]
  );
  const selectableLabelSet = useMemo(() => new Set(selectableLabels), [selectableLabels]);
  const sanitizedSelection = useMemo(
    () => sanitizeSelectionForGrain(selectedCategory, selectionGrain, { availableMarkets: optionAvailableMarkets, industries: activeIndustries }),
    [activeIndustries, optionAvailableMarkets, selectedCategory, selectionGrain]
  );
  const normalizedSelection = useMemo(
    () => sanitizeSelectionForGrain(selectedCategory, selectionGrain, { industries: activeIndustries }),
    [activeIndustries, selectedCategory, selectionGrain]
  );

  useEffect(() => () => {
    if (scrollResetFrameRef.current) cancelAnimationFrame(scrollResetFrameRef.current);
    if (scrollResetTimeoutRef.current) clearTimeout(scrollResetTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (controlledSelectionGrain || internalSelectionGrain === targetSelectionGrain) return;
    setInternalSelectionGrain(targetSelectionGrain);
  }, [controlledSelectionGrain, internalSelectionGrain, targetSelectionGrain]);

  useEffect(() => {
    if (previousSelectionGrainRef.current === selectionGrain) return;
    previousSelectionGrainRef.current = selectionGrain;
    setExpanded(buildExpandedState(selectionGrain));
  }, [selectionGrain]);

  const setDropdownScrollTop = () => {
    if (menuPaperRef.current) menuPaperRef.current.scrollTop = 0;
  };

  const scrollDropdownToTop = () => {
    setDropdownScrollTop();

    if (scrollResetFrameRef.current) cancelAnimationFrame(scrollResetFrameRef.current);
    if (scrollResetTimeoutRef.current) clearTimeout(scrollResetTimeoutRef.current);

    scrollResetFrameRef.current = requestAnimationFrame(() => {
      setDropdownScrollTop();
      scrollResetFrameRef.current = null;
    });
    scrollResetTimeoutRef.current = setTimeout(() => {
      setDropdownScrollTop();
      scrollResetTimeoutRef.current = null;
    }, 0);
  };

  const isExpanded = (key) => expanded[key] || false;
  const toggleExpand = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isMarketAvailable = (market) => !optionAvailableMarketSet || optionAvailableMarketSet.has(market);
  const getVisibleLeaves = (leaves) => leaves.filter(isMarketAvailable);

  const getTargetLabels = (industry, subIndustry, category = null) => {
    return getBulkTargetForNode({
      grain: selectionGrain,
      industry,
      subIndustry,
      category,
      availableMarkets: optionAvailableMarkets,
    });
  };

  useEffect(() => {
    if (!autoNormalizeSelection) return;
    if (!controlledSelectionGrain && selectionGrain !== targetSelectionGrain) return;
    if (normalizedSelection.join('|') !== selectedCategory.join('|')) {
      handleCategoryChange({
        target: { value: normalizedSelection },
        source: CATEGORY_CHANGE_SOURCES.GRAIN_NORMALIZE,
        selectionGrain,
      });
    }
  }, [autoNormalizeSelection, controlledSelectionGrain, handleCategoryChange, normalizedSelection, selectedCategory, selectionGrain, targetSelectionGrain]);

  const toggleLabels = (labels) => {
    const targetLabels = labels.filter(label => selectableLabelSet.has(label));
    if (!targetLabels.length) return;

    const newSelection = toggleCategorySelection(sanitizedSelection, targetLabels);
    handleCategoryChange({
      target: { value: newSelection },
      source: CATEGORY_CHANGE_SOURCES.USER,
      selectionGrain,
    });
  };

  const handleSelectableRowClick = (labels) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleLabels(labels);
  };

  const handleSelectChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const headerBgDefault = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.02)';
  const headerBgHover = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.06)'
    : 'rgba(0, 0, 0, 0.04)';
  const borderColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(0, 0, 0, 0.08)';

  const renderGroupHeader = (label, icon, paddingLeft, showBorder, targetLabels, hasChildren) => {
    const selectableLabels = targetLabels.filter(Boolean);
    const allSelected = selectableLabels.length > 0 && selectableLabels.every(cat => sanitizedSelection.includes(cat));
    const someSelected = selectableLabels.some(cat => sanitizedSelection.includes(cat));
    const isIndeterminate = someSelected && !allSelected;
    const groupExpanded = isExpanded(label);
    const canSelectGroup = selectableLabels.length > 0;

    return (
      <Box
        key={`${label}-header`}
        data-testid={`category-filter-row-${selectionGrain}-${label}`}
        role="button"
        aria-expanded={groupExpanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (canSelectGroup) {
              handleSelectableRowClick(selectableLabels)(e);
            } else if (hasChildren) {
              setExpanded(prev => ({ ...prev, [label]: !prev[label] }));
            }
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '32px',
          pl: paddingLeft,
          pr: 1.5,
          cursor: 'pointer',
          backgroundColor: headerBgDefault,
          borderTop: showBorder ? `1px solid ${borderColor}` : 'none',
          transition: 'background-color 0.1s ease',
          '&:hover': { backgroundColor: headerBgHover },
        }}
        onClick={canSelectGroup
          ? handleSelectableRowClick(selectableLabels)
          : (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (hasChildren) {
                setExpanded(prev => ({ ...prev, [label]: !prev[label] }));
              }
            }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {hasChildren ? (
            <ChevronRightIcon
              onClick={toggleExpand(label)}
              sx={{
                fontSize: 16,
                color: theme.palette.text.secondary,
                transform: groupExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
                cursor: 'pointer',
              }}
            />
          ) : (
            <Box sx={{ width: 16 }} />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: theme.palette.text.secondary }}>
            {icon}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            inputProps={{ 'aria-label': `${label} selection` }}
            data-testid={`category-filter-checkbox-${selectionGrain}-${label}`}
            indeterminate={isIndeterminate}
            size="small"
            checked={allSelected}
            disabled={!canSelectGroup}
            onClick={handleSelectableRowClick(selectableLabels)}
            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
          />
        </Box>
      </Box>
    );
  };

  const renderLeafItem = (label, paddingLeft, parentExpanded) => {
    const isSelected = sanitizedSelection.includes(label);
    const MarketIcon = MARKET_ICON_COMPONENTS[label] || DEFAULT_CATEGORY_ICON;
    const handleLeafClick = handleSelectableRowClick([label]);

    if (!parentExpanded) {
      return <MenuItem key={label} value={label} sx={{ display: 'none' }} />;
    }

    return (
      <MenuItem
        key={label}
        data-testid={`category-filter-row-${selectionGrain}-${label}`}
        value={label}
        onClick={handleLeafClick}
        sx={{
          minHeight: '26px',
          height: '26px',
          py: 0.25,
          pl: paddingLeft,
          pr: 1.5,
          justifyContent: 'space-between',
          backgroundColor: isSelected
            ? theme.palette.mode === 'dark'
              ? 'rgba(145, 166, 205, 0.16)'
              : 'rgba(63, 100, 168, 0.16)'
            : 'transparent',
          borderLeft: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : '2px solid transparent',
          cursor: 'pointer',
          transition: 'background-color 0.1s ease',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: theme.palette.text.secondary }}>
            <MarketIcon />
          </Box>
          <ListItemText
            primary={
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '12px', lineHeight: 1.2 }} variant="body2">
                {label}
              </Typography>
            }
            sx={{ my: 0 }}
          />
        </Box>
        <Checkbox
          inputProps={{ 'aria-label': `${label} selection` }}
          data-testid={`category-filter-checkbox-${selectionGrain}-${label}`}
          size="small"
          checked={isSelected}
          onClick={handleLeafClick}
          sx={{ p: 0, ml: 0.5, '& .MuiSvgIcon-root': { fontSize: 16 } }}
        />
      </MenuItem>
    );
  };

  const buildDisplayValue = (selected) => {
    if (selected.length === 0) {
      return 'Filter by Category';
    }

    const allVisibleLabels = visibleIndustries.flatMap(ind => getTargetLabels(ind));
    if (allVisibleLabels.length > 0 && allVisibleLabels.every(cat => selected.includes(cat))) {
      const scopeLabel = visibleIndustries.length === 1 ? visibleIndustries[0] : 'Sectors';
      return selectionGrain === CATEGORY_GRAINS.MARKET ? `All ${scopeLabel} Markets` : `All ${scopeLabel}`;
    }

    const labels = [];
    for (const ind of visibleIndustries) {
      const indLabels = getTargetLabels(ind);
      if (indLabels.length > 0 && indLabels.every(cat => selected.includes(cat))) {
        labels.push(ind);
        continue;
      }
      for (const sub of SUB_INDUSTRY_ORDER[ind]) {
        const subLabels = getTargetLabels(ind, sub);
        if (subLabels.length === 0) continue;
        if (subLabels.every(cat => selected.includes(cat))) {
          labels.push(sub);
          continue;
        }
        for (const category of CATEGORY_ORDER[ind][sub]) {
          const categoryLabels = getTargetLabels(ind, sub, category);
          if (categoryLabels.length === 0) continue;
          const selectedLabels = categoryLabels.filter(cat => selected.includes(cat));
          if (selectedLabels.length === categoryLabels.length) {
            labels.push(category);
          } else {
            labels.push(...selectedLabels);
          }
        }
      }
    }

    let displayText = labels.join(', ');
    if (displayText.length > 46) {
      displayText = `${displayText.substring(0, 46)}...`;
    }
    return displayText;
  };

  return (
    <Box size="small" sx={{ height: tokens.height, width: controlWidth, display: 'flex', flexDirection: 'row' }}>
      <Select
        size="small"
        sx={{
          backgroundColor: tokens.surface,
          width: controlWidth,
          height: tokens.height,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
          '& .MuiSelect-select': {
            display: 'block',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            pr: '24px !important',
            boxSizing: 'border-box',
          },
        }}
        multiple
        displayEmpty
        value={sanitizedSelection}
        onOpen={scrollDropdownToTop}
        onChange={handleSelectChange}
        renderValue={(selected) => (
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '12px',
              pt: '3px',
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {buildDisplayValue(normalizedSelection)}
          </Typography>
        )}
        MenuProps={buildControlMenuProps(theme, menuContext, {
          variant: 'menu',
          TransitionProps: {
            onEntered: scrollDropdownToTop,
          },
          PaperProps: {
            ref: menuPaperRef,
            'data-testid': 'category-filter-menu-paper',
            elevation: 4,
          },
        })}
      >
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.75,
            backgroundColor: theme.background.transparent,
            backdropFilter: 'blur(8px)',
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: '0 0 auto',
              color: theme.palette.text.secondary,
              fontSize: '10px',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            Select by
          </Typography>
          <ControlToggleGroup
            context="menuToolbar"
            value={selectionGrain}
            onChange={(_, nextGrain) => {
              if (!nextGrain) return;
              const next = sanitizeSelectionForGrain(
                normalizeCategorySelection(sanitizedSelection, optionAvailableMarkets, { grain: nextGrain, fallback: [] }),
                nextGrain,
                { availableMarkets: optionAvailableMarkets, industries: activeIndustries }
              );
              setSelectionGrain(nextGrain);
              setExpanded(buildExpandedState(nextGrain));
              scrollDropdownToTop();
              handleCategoryChange({
                target: { value: next },
                source: CATEGORY_CHANGE_SOURCES.GRAIN_TOGGLE,
                selectionGrain: nextGrain,
              });
            }}
            options={selectionGrainOptions}
            buttonWidth={72}
            mode="text"
            ariaLabel="Select category depth"
          />
        </Box>
        {sanitizedSelection
          .filter(label => getCategoryLabelType(label) !== CATEGORY_GRAINS.MARKET)
          .map(label => (
            <MenuItem key={`${label}-selected-hidden`} value={label} sx={{ display: 'none' }} />
          ))}
        {visibleIndustries.flatMap((ind, indIdx) => {
          const indExpanded = isExpanded(ind);
          const items = [];

          items.push(renderGroupHeader(
            ind,
            CATEGORY_GROUP_ICONS[ind],
            1.5,
            indIdx > 0,
            getTargetLabels(ind),
            true
          ));

          for (const sub of SUB_INDUSTRY_ORDER[ind]) {
            const subLeaves = getVisibleLeaves(getSubIndustryCategories(ind, sub));
            if (subLeaves.length === 0) continue;
            const subExpanded = isExpanded(sub);

            if (!indExpanded || selectionGrain === CATEGORY_GRAINS.INDUSTRY) {
              items.push(
                <Box key={`${sub}-hidden`} sx={{ display: 'none' }}>
                  {subLeaves.map(market => (
                    <MenuItem key={market} value={market} sx={{ display: 'none' }} />
                  ))}
                </Box>
              );
              continue;
            }

            const categoryLabels = CATEGORY_ORDER[ind][sub];
            const hasDuplicateSingleCategory = categoryLabels.length === 1 && categoryLabels[0] === sub;

            if (!(selectionGrain === CATEGORY_GRAINS.CATEGORY && hasDuplicateSingleCategory)) {
              items.push(renderGroupHeader(
                sub,
                CATEGORY_GROUP_ICONS[sub],
                3,
                false,
                getTargetLabels(ind, sub),
                selectionGrain !== CATEGORY_GRAINS.SUB_INDUSTRY
              ));
            }

            for (const category of categoryLabels) {
              const categoryLeaves = getVisibleLeaves(getCategoryMarkets(ind, sub, category));
              if (categoryLeaves.length === 0) continue;
              const categoryExpanded = isExpanded(category);

              if (!subExpanded || selectionGrain === CATEGORY_GRAINS.SUB_INDUSTRY) {
                items.push(
                  <Box key={`${category}-hidden`} sx={{ display: 'none' }}>
                    {categoryLeaves.map(market => (
                      <MenuItem key={market} value={market} sx={{ display: 'none' }} />
                    ))}
                  </Box>
                );
                continue;
              }

              const hideDuplicateCategoryHeader = hasDuplicateSingleCategory && isMarketGrain;

              if (!hideDuplicateCategoryHeader) {
                items.push(renderGroupHeader(
                  category,
                  CATEGORY_GROUP_ICONS[category],
                  hasDuplicateSingleCategory ? 3 : 4.5,
                  false,
                  getTargetLabels(ind, sub, category),
                  isMarketGrain
                ));
              }

              if (isMarketGrain) {
                const leafExpanded = hideDuplicateCategoryHeader ? subExpanded : categoryExpanded;
                items.push(...categoryLeaves.map(market => renderLeafItem(market, hideDuplicateCategoryHeader ? 4.5 : 6.5, leafExpanded)));
              }
            }
          }

          return items;
        })}
      </Select>
    </Box>
  );
};

export default FilterCategory;
